// frontend/src/pages/EmailSettingsPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function EmailSettingsPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_secure: 0, // 0 or 1
    smtp_user: "",
    smtp_pass: "",
    smtp_from_name: "",
    smtp_from_email: "",
    business_notify_email: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [msg, setMsg] = useState(null); // { text, error? }

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/settings/email");
        if (!r.ok) throw new Error(await r.text());
        const data = await r.json();
        setForm({
          smtp_host: data.smtp_host || "",
          smtp_port: Number(data.smtp_port ?? 587),
          smtp_secure: Number(data.smtp_secure ?? 0),
          smtp_user: data.smtp_user || "",
          smtp_pass: data.smtp_pass || "",
          smtp_from_name: data.smtp_from_name || "",
          smtp_from_email: data.smtp_from_email || "",
          business_notify_email: data.business_notify_email || "",
        });
      } catch (e) {
        setMsg({ text: `Failed to load email settings: ${e.message || e}`, error: true });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const r = await fetch("/api/settings/email", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      setMsg({ text: "Email settings saved." });
    } catch (e) {
      setMsg({ text: `Save failed: ${e.message || e}`, error: true });
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (!testTo) {
      setMsg({ text: "Enter a test recipient address.", error: true });
      return;
    }
    setTesting(true);
    setMsg(null);
    try {
      const r = await fetch("/api/settings/email/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: testTo }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      setMsg({ text: "Test email sent." });
    } catch (e) {
      setMsg({ text: `Test failed: ${e.message || e}`, error: true });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <h2 className="section-title" style={{ margin: 0 }}>Email Server</h2>
        <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0 12px" }} />

        {loading ? (
          <div>Loading…</div>
        ) : (
          <>
            {/* Form */}
            <div
              className="form-grid"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}
            >
              <div>
                <div className="label">SMTP host</div>
                <input
                  className="input"
                  value={form.smtp_host}
                  onChange={e => setField("smtp_host", e.target.value)}
                />
              </div>

              <div>
                <div className="label">SMTP port</div>
                <input
                  className="input"
                  type="number"
                  value={form.smtp_port}
                  onChange={e => setField("smtp_port", Number(e.target.value || 0))}
                />
              </div>

              <div>
                <div className="label">Use SSL (implicit TLS on 465)</div>
                <label className="row" style={{ alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={Number(form.smtp_secure) === 1}
                    onChange={e => setField("smtp_secure", e.target.checked ? 1 : 0)}
                  />
                  <span>Secure connection</span>
                </label>
              </div>

              <div>
                <div className="label">SMTP username</div>
                <input
                  className="input"
                  value={form.smtp_user}
                  onChange={e => setField("smtp_user", e.target.value)}
                />
              </div>

              <div>
                <div className="label">SMTP password</div>
                <input
                  className="input"
                  type="password"
                  value={form.smtp_pass}
                  onChange={e => setField("smtp_pass", e.target.value)}
                />
              </div>

              <div>
                <div className="label">From name</div>
                <input
                  className="input"
                  value={form.smtp_from_name}
                  onChange={e => setField("smtp_from_name", e.target.value)}
                />
              </div>

              <div>
                <div className="label">From email</div>
                <input
                  className="input"
                  value={form.smtp_from_email}
                  onChange={e => setField("smtp_from_email", e.target.value)}
                />
              </div>

              <div>
                <div className="label">CC business notifications to</div>
                <input
                  className="input"
                  value={form.business_notify_email}
                  onChange={e => setField("business_notify_email", e.target.value)}
                />
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
              <input
                className="input"
                placeholder="test recipient (you@domain.com)"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              <button className="btn secondary" onClick={sendTest} disabled={testing}>
                {testing ? "Sending…" : "Send test"}
              </button>
              <button className="btn" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button type="button" className="btn cancel" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>

            {msg && (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${msg.error ? "#fecaca" : "#cce3d1"}`,
                  background: msg.error ? "#fee2e2" : "#e7f6ec",
                  color: msg.error ? "#991b1b" : "#135f2b",
                }}
              >
                {msg.text}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

